import React, { useEffect, useRef, useState } from "react";
import { ActivityCard, ChoiceButton, MixedText, ProgressPill, useShuffledChoices } from "../shared";

const AR_RE = /[؀-ۿ]/;
const LATIN_RE = /[A-Za-z]/;

/**
 * A choice is laid out RTL only when it is Arabic through and through. An
 * English answer that quotes a letter or a word ("It unties into a regular
 * ت") stays LTR and renders its Arabic runs through MixedText — flipping the
 * whole button to rtl would reorder the English around the quoted Arabic.
 */
const isArabicOnly = (choice) => AR_RE.test(choice) && !LATIN_RE.test(choice);

/**
 * GrammarLessonQuiz — "التَّحَدِّي" final scored challenge.
 *
 * Runs the mixed question set (grammar_mc / audio_mc), reports the score to
 * the parent exactly once per run, and offers retakes (best score is kept
 * server-side). audio_mc falls back to showing the Arabic text when audio
 * isn't available.
 */
export default function GrammarLessonQuiz({ quiz, progress, onSubmitScore, audio }) {
  const [phase, setPhase] = useState("intro"); // intro | question | results
  const [qIdx, setQIdx] = useState(0);
  const [run, setRun] = useState(0); // bumps each (re)take → fresh shuffles
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const [audioFailed, setAudioFailed] = useState(false);
  const submittedRef = useRef(false);

  const questions = quiz.questions;
  const question = questions[qIdx];
  const view = useShuffledChoices(question.choices, question.correct_index, [question, run]);
  const scorePercent = Math.round((correctCount / questions.length) * 100);
  const passed = scorePercent >= quiz.pass_percent;

  useEffect(() => {
    if (phase !== "results" || submittedRef.current) return;
    submittedRef.current = true;
    Promise.resolve(onSubmitScore(scorePercent)).catch((e) => setSaveError(e.message || "Could not save your score."));
  }, [phase, scorePercent, onSubmitScore]);

  // Auto-play audio questions when they appear; if playback fails, reveal the
  // Arabic text instead so the question stays answerable.
  useEffect(() => {
    setAudioFailed(false);
    if (phase === "question" && question?.type === "audio_mc" && audio.audioAllowed) {
      Promise.resolve(audio.play(question.audio_text || question.prompt_ar)).then(
        (ok) => ok === false && setAudioFailed(true)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, qIdx]);

  const start = () => {
    setPhase("question");
    setQIdx(0);
    setRun((n) => n + 1);
    setPicked(null);
    setCorrectCount(0);
    setSaveError(null);
    submittedRef.current = false;
  };

  const handlePick = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    if (idx === view.correctIndex) setCorrectCount((n) => n + 1);
  };

  const next = () => {
    setPicked(null);
    if (qIdx + 1 >= questions.length) setPhase("results");
    else setQIdx((i) => i + 1);
  };

  if (phase === "intro") {
    return (
      <ActivityCard>
        <div className="text-center py-6">
          <p dir="rtl" lang="ar" className="font-arabic text-4xl m-0" style={{ color: "var(--gold-bright)" }}>
            {quiz.title_arabic}
          </p>
          <p className="font-cormorant font-semibold text-2xl mt-1.5 mb-2" style={{ color: "var(--parchment)" }}>
            {quiz.title}
          </p>
          <p className="text-sm max-w-md mx-auto mb-4" style={{ color: "var(--parchment-dim)" }}>{quiz.intro}</p>
          {progress.quiz_best_score > 0 && (
            <p className="text-sm mb-4" style={{ color: progress.quiz_passed ? "var(--teal)" : "var(--parchment)" }}>
              Best score: {progress.quiz_best_score}%{progress.quiz_passed ? " — passed" : ""}
            </p>
          )}
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center justify-center px-6 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            {progress.quiz_best_score > 0 ? "Retake the challenge" : "Start the challenge"}
          </button>
        </div>
      </ActivityCard>
    );
  }

  if (phase === "results") {
    return (
      <ActivityCard>
        <div className="text-center py-6">
          <p className="text-4xl m-0" aria-hidden="true">{passed ? "🏆" : "📚"}</p>
          <p className="font-cormorant font-semibold text-3xl mt-3 mb-1" style={{ color: passed ? "var(--teal)" : "var(--parchment)" }}>
            {scorePercent}%
          </p>
          <p className="text-sm mb-1" style={{ color: "var(--parchment-dim)" }}>
            {correctCount} of {questions.length} correct · pass mark {quiz.pass_percent}%
          </p>
          <p className="text-sm mb-4" style={{ color: "var(--parchment)" }}>
            {passed
              ? quiz.pass_message || "Lesson complete! 🎉"
              : "Not quite — review any section above, then try again."}
          </p>
          {saveError && (
            <p className="text-xs mb-3" style={{ color: "#e58c8c" }}>{saveError}</p>
          )}
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center justify-center px-5 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={passed
              ? { border: "1px solid var(--panel-line)", color: "var(--parchment)", background: "transparent" }
              : { background: "var(--gold)", color: "var(--ink)" }}
          >
            Retake
          </button>
        </div>
      </ActivityCard>
    );
  }

  const isAudioQuestion = question.type === "audio_mc";
  const showArabicPrompt = question.prompt_ar && (!isAudioQuestion || !audio.audioAllowed || audioFailed);

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-5">
        <ProgressPill>Question {qIdx + 1}/{questions.length}</ProgressPill>
        <span className="text-sm" style={{ color: "var(--parchment-dim)" }}>{correctCount} correct</span>
      </div>

      <p className="text-base text-center font-medium mb-2" style={{ color: "var(--parchment)" }}>
        {question.prompt_en}
      </p>

      {isAudioQuestion && audio.audioAllowed && (
        <div className="flex justify-center mb-4">
          <button
            type="button"
            onClick={() => audio.play(question.audio_text || question.prompt_ar)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-[filter] hover:brightness-110"
            style={{ background: "var(--violet-bg)", color: "var(--tag-violet)", border: "1px solid var(--panel-line)" }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7L8 5z" />
            </svg>
            {audio.playingText ? "Playing…" : "Play again"}
          </button>
        </div>
      )}

      {showArabicPrompt && (
        <p dir="rtl" lang="ar" className="font-arabic text-4xl leading-[1.9] text-center mt-3 mb-4" style={{ color: "var(--parchment)" }}>
          {question.prompt_ar}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl mx-auto mt-4">
        {view.choices.map((choice, idx) => {
          let state = "idle";
          if (picked !== null) {
            if (idx === view.correctIndex) state = "correct";
            else if (idx === picked) state = "wrong";
            else state = "dim";
          }
          const rtl = isArabicOnly(choice);
          return (
            <ChoiceButton key={idx} arabic={rtl} state={state} disabled={picked !== null} onClick={() => handlePick(idx)}>
              {rtl ? choice : <MixedText text={choice} />}
            </ChoiceButton>
          );
        })}
      </div>

      {picked !== null && (
        <div className="flex justify-center mt-5">
          <button
            type="button"
            onClick={next}
            className="inline-flex items-center justify-center px-5 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            {qIdx + 1 >= questions.length ? "See results" : "Next question"}
          </button>
        </div>
      )}
    </ActivityCard>
  );
}
